/**
 * Who supplies this item: the Supplied Item rows of every supplier record that names it.
 *
 * PHASE 4 OF THE PURCHASE ORDER BUILD-OUT (Ed, 2026-10-30: "supplier items on the Item"). The fact
 * lives on the SUPPLIER's form -- its Supplied Items table is the approved supplier list -- and the
 * Item reads it here: supplier, ASL status, their part number, the manufacturer and its part number when
 * the supplier is a distributor, price, lead time, buy unit, use unit, use units per buy unit, and which
 * one is the primary (first). Nothing is edited here: the row is
 * the supplier's and is edited on the supplier's Form tab, where one primary per item is enforced.
 *
 * UNDER THE READER'S OWN SIGHT: a supplier record the reader may not open is its ID, grey, no link.
 * Rows whose ASL status is not an approving one are shown too, marked, so a buyer sees the whole
 * picture; the list is advisory (the decision of 2026-10-22), and nothing here enforces it.
 */
import React, {useCallback, useContext, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Box, Typography} from "@mui/material";
import {AppContext} from "../../util/AppContext";
import {makeCresAxios} from "../../util/Axios";
import {Blue3, Grey2, Grey6, Grey7, Grey8, Grey17} from "../../theme/styles";

type Row = {SupplierObjectUSID: string; SupplierObjectID?: string | null; SupplierName?: string | null; SupplierRevisionUSID?: string;
            Visible?: boolean; ASLStatus?: string | null; Approved?: boolean; SupplierPartNumber?: string | null;
            UnitPrice?: {Amount: string; Currency: string} | null; LeadTimeDays?: number | null; BuyUnit?: string | null;
            UseUnit?: string | null; Conversion?: string | null; Primary?: boolean; QualificationExpires?: string | null;
            ManufacturerObjectID?: string | null; ManufacturerName?: string | null; ManufacturerPartNumber?: string | null};

function listOf<T>(held: T[] | T | undefined | null): T[] {
    if (held === null || held === undefined) return [];
    return Array.isArray(held) ? held : [held];
}

export default function ItemSuppliersPane({objectUSID}: {objectUSID: string}) {
    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);
    const navigate = useNavigate();
    const [rows, setRows] = useState<Row[] | null>(null);
    const [problem, setProblem] = useState<string | null>(null);

    const load = useCallback(() => {
        if (!objectUSID) return;
        api.get("t1/v1/approved_suppliers", {params: {ItemObjectUSID: objectUSID, IncludeUnapproved: true}})
            .then((response: any) => {
                setRows(listOf<Row>(response?.data?.PlatformMessage?.ListOfApprovedSupplier?.ApprovedSupplier));
                setProblem(null);
            })
            .catch((failure: any) => { setRows([]); setProblem(failure?.response?.data?.Cause ?? "The suppliers could not be read."); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objectUSID]);
    useEffect(() => { load(); }, [load]);

    const money = (price?: Row["UnitPrice"]) => price ? `${price.Amount} ${price.Currency}` : "—";
    const hidden = (rows ?? []).filter(one => one.Visible === false).length;

    return (
        <Box sx={{border: `1px solid ${Grey2}`, borderRadius: "6px", padding: "12px 14px"}}>
            <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8, marginBottom: "4px"}}>
                Suppliers{rows ? ` (${rows.length})` : ""}
            </Typography>
            <Typography sx={{fontSize: 12.5, color: Grey7, marginBottom: "10px"}}>
                Read from each supplier's own Supplied Items; edit it there. The primary is first.
            </Typography>
            {problem && <Typography sx={{fontSize: 13, color: "#B3261E", marginBottom: "8px"}}>{problem}</Typography>}
            {rows === null && <Typography sx={{fontSize: 13, color: Grey7}}>Reading…</Typography>}
            {rows && rows.length === 0 && (
                <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6}}>No supplier record names this item yet.</Typography>
            )}
            {rows && rows.length > 0 && (
                <Box sx={{display: "grid", gridTemplateColumns: "2fr 0.9fr 1.1fr 1.4fr 1.1fr 1fr 0.7fr 0.7fr 0.7fr 0.8fr", gap: "6px 12px", alignItems: "center"}}>
                    {["Supplier", "ASL", "Their part no.", "Manufacturer", "Mfr part no.", "Price", "Lead (d)", "Buy unit", "Use unit", "Per buy unit"].map((head, index) => (
                        <Typography key={`${head}${index}`} sx={{fontSize: 11.5, color: Grey7, textTransform: "uppercase", letterSpacing: "0.04em"}}>
                            {head}
                        </Typography>
                    ))}
                    {rows.map(row => row.Visible === false ? (
                        <React.Fragment key={row.SupplierObjectUSID}>
                            <Typography sx={{fontSize: 13, color: Grey6}} title="This supplier is not visible to you.">{row.SupplierObjectID ?? "—"}</Typography>
                            <Typography sx={{fontSize: 13, color: Grey6, fontStyle: "italic", gridColumn: "span 9"}}>Not visible to you</Typography>
                        </React.Fragment>
                    ) : (
                        <React.Fragment key={row.SupplierObjectUSID}>
                            <Typography sx={{fontSize: 13, color: Blue3, cursor: "pointer"}}
                                        onClick={() => row.SupplierRevisionUSID && navigate(`/object/${row.SupplierRevisionUSID}`)}>
                                {row.Primary ? "★ " : ""}{row.SupplierObjectID ?? ""} {row.SupplierName ?? ""}
                            </Typography>
                            <Typography sx={{fontSize: 12.5, color: row.Approved ? Grey17 : "#B54708"}}>{row.ASLStatus ?? "—"}</Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>{row.SupplierPartNumber ?? "—"}</Typography>
                            {/* WHOSE PART IT IS when the supplier is a distributor, and the maker's number (2026-10-31). */}
                            <Typography sx={{fontSize: 12.5, color: Grey8}} title={row.ManufacturerObjectID ?? undefined}>{row.ManufacturerName ?? row.ManufacturerObjectID ?? "—"}</Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>{row.ManufacturerPartNumber ?? "—"}</Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>{money(row.UnitPrice)}</Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>{row.LeadTimeDays ?? "—"}</Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>{row.BuyUnit ?? "—"}</Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>{row.UseUnit ?? "—"}</Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>{row.Conversion ?? "—"}</Typography>
                        </React.Fragment>
                    ))}
                </Box>
            )}
            {hidden > 0 && (
                <Typography sx={{fontSize: 12.5, color: Grey7, marginTop: "8px"}}>
                    {hidden} {hidden === 1 ? "supplier is" : "suppliers are"} not visible to you.
                </Typography>
            )}
        </Box>
    );
}
